import {
  PredefinitionPropertyHandler
} from "@ingka/webc-tools";
import SkapaElement from "@ingka/skapa-webc-element";
import { IconButton } from "@ingka/button-webc/passive.js";
import { Icon } from "@ingka/icon-webc/passive.js";
import {
  AspectRatioBox
} from "@ingka/aspect-ratio-box-webc/passive.js";
import { Image } from "@ingka/image-webc/passive.js";
import { arrowRight } from "@ingka/icon-store";
const styles = ':host{cursor:pointer;display:block}*{box-sizing:border-box}skapa-aspect-ratio{display:flex}.image>*{min-height:100%}a{display:flex;flex-direction:column;height:100%;text-decoration:none}a:not(.with-image){border-top:1px solid rgb(var(--colour-neutral-3,223,223,223))}a:not(.with-image) .image{display:none}.info{display:flex;flex-direction:column;padding:1.5rem;padding-inline-start:0}:focus-visible [name=title]::slotted(*),:host(:hover) [name=title]::slotted(*){text-decoration:underline}a:focus:not(:focus-visible){outline:none}a:focus-visible{box-shadow:0 0 0 4px var(--skapa-focus-ring-internal,rgb(var(--colour-neutral-1,255,255,255)));outline:2px solid var(--skapa-focus-ring-external,rgb(var(--colour-neutral-7,17,17,17)));outline-offset:4px;transition:outline .2s ease-out,box-shadow .2s ease-out}[name=image]::slotted(*){max-height:100%;max-width:100%;min-height:100%;object-fit:cover;width:100%}[name=title]{display:block;margin-top:.5rem}[name=title]::slotted(*){color:rgb(var(--colour-text-and-icon-1,17,17,17));font-size:1.125rem;letter-spacing:-.0042em;line-height:1.444}p{color:rgb(var(--colour-text-and-icon-2,72,72,72));margin:1rem 0 0;padding:0}[name=cta]{display:inline-block;margin-top:1.5rem}[name=label]::slotted(*){color:rgb(var(--colour-text-and-icon-2,72,72,72));font-size:.75rem;line-height:1.5}:host(:not([emphasised])) #btn{display:none}:host(:not([emphasised])) #btn+*{color:rgb(var(--colour-text-and-icon-1,17,17,17));margin-top:-.5rem}:host([emphasised]){background-color:rgb(var(--colour-neutral-2,245,245,245))}:host([emphasised]) a{border-top:0}:host([emphasised]) .info{flex-grow:1;padding-inline-start:1.5rem}:host([emphasised]) #btn{margin-top:1.5rem}:host([emphasised]) #btn+[icon]{display:none}:host([emphasised]) .text-wrapper{flex-grow:1}:host([large]){font-size:1rem;line-height:1.625}:host([large]) [name=title]::slotted(*){font-size:1.5rem;line-height:1.45}:host([compact]) .image{flex-shrink:0}:host([compact]) .info{flex-grow:1}:host([compact]) .text-wrapper{max-width:30rem}@media (min-width:37.5em){:host(:not([compact])) .info{padding-inline-end:10rem}:host(:not([compact])):host([emphasised]) .info{padding:2.5rem;padding-inline-end:7.5rem}:host(:not([compact])):host([emphasised]) [name=cta]{margin-top:1.5rem}:host(:not([compact])) [name=title]::slotted(*){font-size:1.5rem;line-height:1.45}:host(:not([compact])):host([large]) [name=title]::slotted(*){font-size:2.25rem;line-height:1.333}:host(:not([compact])):host([emphasised]) #btn{margin-top:2.5rem}}@media (min-width:75em){:host(:not([compact])) a{flex-direction:row}:host(:not([compact])) .image{flex-basis:60%;max-width:60%}:host(:not([compact])) .info{padding-bottom:0;padding-top:0}:host(:not([compact])) a.with-image .info{flex-basis:40%;max-width:40%;padding-inline-end:4rem;padding-inline-start:3rem}:host(:not([compact])) .text-wrapper{max-width:37.5rem}:host(:not([compact])):host([trailing-image]) .image{order:1}:host(:not([compact])):host([emphasised]) .info{padding:3rem;padding-inline-end:4rem}:host(:not([compact])):host([emphasised]) [name=cta]{margin-top:1.5rem}}:host([inverse]) [name=label]::slotted(*),:host([inverse]) [name=title]::slotted(*),:host([inverse]) p{color:inherit}:host([inverse]) .info{color:#fff}';
const lightStyles = 'skapa-card[inverse]>*{color:inherit}';
function generateTemplate({ html, prefixReplacer }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <a>
      <div class="image">
        <skapa-aspect-ratio ratio="wide" id="aspectratio">
          <skapa-image>
            <slot name="image"></slot>
          </skapa-image>
        </skapa-aspect-ratio>
      </div>
      <div class="info">
        <div class="text-wrapper">
          <slot name="label"></slot>
          <slot name="title"></slot>
          <p><slot></slot></p>
        </div>
        <slot name="cta">
          <skapa-icon-button
            id="btn"
            inverse
            static-colour
            decorative-only
            size="small"
            ><skapa-icon slot="icon" icon="arrow-right" flip-rtl></skapa-icon
          ></skapa-icon-button>
          <skapa-icon icon="arrow-right" flip-rtl></skapa-icon>
        </slot>
      </div>
    </a>
    <slot name="light-styles"></slot>
  `;
  const lightStyleTemplate = document.createElement("style");
  lightStyleTemplate.slot = "light-styles";
  lightStyleTemplate.innerHTML = prefixReplacer(lightStyles.toString());
  return { main: template, lightStyleTemplate };
}
class Card extends SkapaElement {
  constructor() {
    super();
    this.handleImageMode = () => {
      this.anchor.classList.toggle(
        "with-image",
        Boolean(this.imageSlot.assignedNodes({ flatten: true }).length)
      );
    };
    this.syncInverseLightStyle = () => {
      if (this.inverse)
        this.append(this.__lightStyleNode);
      else
        this.__lightStyleNode.remove();
    };
    PredefinitionPropertyHandler.gatherProperties(
      [
        "url",
        "emphasised",
        "large",
        "compact",
        "target",
        "inverse",
        "trailingImage",
        "imageRatio"
      ],
      this
    );
    const shadow = this.shadow = this.attachShadow({
      mode: "open",
      delegatesFocus: true
    });
    const templates = this.getTemplates(generateTemplate);
    shadow.append(templates.main.content.cloneNode(true));
    this.imageSlot = shadow.querySelector("[name=image]");
    this.imageSlot.addEventListener("slotchange", this.handleImageMode);
    this.aspectRatio = shadow.getElementById("aspectratio");
    this.__lightStyleNode = templates.lightStyleTemplate.cloneNode(
      true
    );
  }
  connectedCallback() {
    PredefinitionPropertyHandler.initGatheredProperties(this);
    this.syncInverseLightStyle();
  }
  get anchor() {
    return this.shadow.querySelector("a");
  }
  get emphasised() {
    return this.hasAttribute("emphasised");
  }
  set emphasised(toggle) {
    this.toggleAttribute("emphasised", !!toggle);
  }
  get large() {
    return this.hasAttribute("large");
  }
  set large(toggle) {
    this.toggleAttribute("large", !!toggle);
  }
  get compact() {
    return this.hasAttribute("compact");
  }
  set compact(toggle) {
    this.toggleAttribute("compact", !!toggle);
  }
  get trailingImage() {
    return this.hasAttribute("trailing-image");
  }
  set trailingImage(toggle) {
    this.toggleAttribute("trailing-image", !!toggle);
  }
  // Forwarded properties
  set url(value) {
    if (value)
      this.setAttribute("url", value);
    else
      this.removeAttribute("url");
  }
  get url() {
    return this.getAttribute("url");
  }
  set target(value) {
    if (value)
      this.setAttribute("target", value);
    else
      this.removeAttribute("target");
  }
  get target() {
    return this.anchor.getAttribute("target");
  }
  get inverse() {
    return this.hasAttribute("inverse");
  }
  set inverse(toggle) {
    this.toggleAttribute("inverse", !!toggle);
  }
  set imageRatio(value) {
    if (value)
      this.aspectRatio.ratio = value;
    else
      this.aspectRatio.ratio = "wide";
  }
  get imageRatio() {
    const attribute = this.aspectRatio.getAttribute(
      "ratio"
    );
    return attribute || "wide";
  }
  attributeChangedCallback(name, oldval, newVal) {
    if (name === "url") {
      if (newVal) {
        this.anchor.setAttribute("href", newVal);
      } else {
        this.anchor.removeAttribute("href");
      }
    } else if (name === "target") {
      if (newVal) {
        this.anchor.setAttribute("target", newVal);
      } else {
        this.anchor.removeAttribute("target");
      }
    } else if (name === "image-ratio") {
      if (newVal) {
        this.aspectRatio.ratio = newVal;
      } else {
        this.aspectRatio.ratio = "wide";
      }
    } else if (name === "inverse") {
      this.syncInverseLightStyle();
    }
  }
}
Card.skapaTagName = "card";
Card.skapaDependencies = [IconButton, Icon, AspectRatioBox, Image];
Card.skapaIconDependencies = [arrowRight];
Card.observedAttributes = [
  "url",
  "inverse",
  "target",
  "image-ratio"
];
export {
  Card
};
